/**
 * DIŞARIDAN İÇE AKTARMA — dosyadan hareket satırlarına (09.10.2026).
 * Biçim ve kararlar: `claude/notlar/2026-10-09-ice-aktarma-onerisi.md`.
 *
 * Bu modül SAF: dosya metnini okur, satırları anlar, sorunları söyler,
 * kalıcı kimlikleri türetir. Ağ (katalog eşleştirme) ve yazım ekranın işi.
 *
 * İLKELER:
 *  - Tahmin yok. Anlaşılmayan değer satırı "sorunlu" yapar, yerine bir şey
 *    konmaz. Sayı biçimi (1.234,56 / 1,234.56) KULLANICI seçer; tahmin yalnızca
 *    öneridir.
 *  - Para `number` ile hesaplanmaz: miktar × fiyat ölçekli tam sayıyla.
 *  - Kimlik kalıcıdır: aynı kaynak satırı her içe aktarmada AYNI Parafon
 *    kimliğini alır (kaynak kimliği varsa ondan, yoksa satırın içeriğinden ve
 *    dosyadaki sırasından).
 */

import { CURRENCIES, isKnownCurrency } from "@parafon/contracts";
import type { Decimal, IsoDate, TransactionType } from "@parafon/contracts";
import { isValidDecimal } from "@parafon/contracts/local-api";
import { M } from "../dil";

// ---------------------------------------------------------------------------
// Metin kodlaması
// ---------------------------------------------------------------------------

/**
 * Dosya baytlarından metin: geçerli UTF-8 ise o; değilse Türkçe Windows
 * (windows-1254) — Türkçe Excel'in "CSV olarak kaydet"i böyle yazar.
 */
export function metniCoz(baytlar: Uint8Array): { readonly metin: string; readonly kodlama: "utf-8" | "windows-1254" } {
  try {
    return { metin: new TextDecoder("utf-8", { fatal: true }).decode(baytlar), kodlama: "utf-8" };
  } catch {
    return { metin: new TextDecoder("windows-1254").decode(baytlar), kodlama: "windows-1254" };
  }
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

export type Ayrac = ";" | "," | "\t";

/** İlk satırdaki (tırnak dışı) en sık ayraç. Türkçe Excel `;` yazar. */
export function ayracBul(metin: string): Ayrac {
  const ilk = metin.replace(/^﻿/, "").split(/\r?\n/, 1)[0] ?? "";
  const say = { ";": 0, ",": 0, "\t": 0 } as Record<Ayrac, number>;
  let tirnak = false;
  for (const c of ilk) {
    if (c === '"') tirnak = !tirnak;
    else if (!tirnak && (c === ";" || c === "," || c === "\t")) say[c] += 1;
  }
  const sirali = (Object.keys(say) as Ayrac[]).sort((a, b) => say[b] - say[a]);
  return say[sirali[0] as Ayrac] > 0 ? (sirali[0] as Ayrac) : ";";
}

/** RFC 4180'e yakın okuma: tırnaklı alan, alan içinde satır sonu, `""` kaçışı. Boş satırlar atlanır. */
export function csvOku(metin: string, ayrac: Ayrac = ayracBul(metin)): string[][] {
  const s = metin.replace(/^﻿/, "");
  const satirlar: string[][] = [];
  let alan = "";
  let satir: string[] = [];
  let tirnak = false;
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i] as string;
    if (tirnak) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          alan += '"';
          i += 1;
        } else tirnak = false;
      } else alan += c;
      continue;
    }
    if (c === '"' && alan === "") tirnak = true;
    else if (c === ayrac) {
      satir.push(alan);
      alan = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && s[i + 1] === "\n") i += 1;
      satir.push(alan);
      if (satir.some((a) => a.trim() !== "")) satirlar.push(satir);
      satir = [];
      alan = "";
    } else alan += c;
  }
  satir.push(alan);
  if (satir.some((a) => a.trim() !== "")) satirlar.push(satir);
  return satirlar;
}

/** Sorunlu satırları geri vermek için: başlık + satırlar, aynı ayraçla. */
export function csvYaz(satirlar: readonly (readonly string[])[], ayrac: Ayrac = ";"): string {
  const kacir = (a: string): string => (/[";,\t\r\n]/.test(a) ? `"${a.replace(/"/g, '""')}"` : a);
  return `${satirlar.map((s) => s.map(kacir).join(ayrac)).join("\r\n")}\r\n`;
}

// ---------------------------------------------------------------------------
// Sayı ve tarih
// ---------------------------------------------------------------------------

/** `tr`: 1.234,56 · `en`: 1,234.56. */
export type SayiBicimi = "tr" | "en";

/** Boşsa `null`; okunamazsa `undefined`. Sonuç sözleşmedeki `Decimal` (nokta ondalık). */
export function sayiOku(ham: string, bicim: SayiBicimi): Decimal | null | undefined {
  let s = ham.replace(/[\s  ]/g, "").replace(/^[−–]/, "-");
  if (s === "") return null;
  const binlik = bicim === "tr" ? "." : ",";
  const ondalik = bicim === "tr" ? "," : ".";
  if (s.includes(binlik)) {
    const [tam = "", ...geri] = s.split(ondalik);
    if (geri.length > 1) return undefined;
    // Binlik gruplar üçlü olmalı: "1.23" tr biçiminde bir hata, bin değil.
    const isaretsiz = tam.replace(/^-/, "");
    if (!/^\d{1,3}(\.\d{3})+$|^\d{1,3}(,\d{3})+$/.test(isaretsiz)) return undefined;
    s = tam.split(binlik).join("") + (geri.length === 1 ? `.${geri[0] as string}` : "");
  } else if (ondalik === ",") s = s.replace(",", ".");
  if (/^-?\./.test(s)) s = s.replace(".", "0.");
  return isValidDecimal(s) ? s : undefined;
}

/**
 * Sayı biçimi ÖNERİSİ: bir değerde hem nokta hem virgül varsa sondaki
 * ondalıktır; yalnızca virgüllü değerler (1,5) Türkçeyi gösterir. Kanıt
 * yoksa `null` (kullanıcı seçer).
 */
export function sayiBicimiTahmin(degerler: Iterable<string>): SayiBicimi | null {
  let tr = 0;
  let en = 0;
  for (const ham of degerler) {
    const s = ham.trim();
    const n = s.lastIndexOf(".");
    const v = s.lastIndexOf(",");
    if (n >= 0 && v >= 0) {
      if (v > n) tr += 1;
      else en += 1;
    } else if (v >= 0 && !/,\d{3}$/.test(s)) tr += 1;
    else if (n >= 0 && !/\.\d{3}$/.test(s)) en += 1;
  }
  if (tr > 0 && en === 0) return "tr";
  if (en > 0 && tr === 0) return "en";
  return null;
}

/** `GG.AA.YYYY`, `GG/AA/YYYY`, `YYYY-AA-GG` (ardından saat gelirse atılır). Geçersiz takvim günü `null`. */
export function tarihOku(ham: string): IsoDate | null {
  const s = ham.trim().split(/[ T]/)[0] ?? "";
  let y: string | undefined;
  let m: string | undefined;
  let d: string | undefined;
  let e = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (e !== null) [, y, m, d] = e;
  else {
    e = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/.exec(s);
    if (e !== null) [, d, m, y] = e;
  }
  if (y === undefined || m === undefined || d === undefined) return null;
  const iso = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  const t = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(t.getTime()) || t.toISOString().slice(0, 10) !== iso ? null : iso;
}

// ---------------------------------------------------------------------------
// Ölçekli ondalık (12 hane) — miktar × fiyat
// ---------------------------------------------------------------------------

const OLCEK = 12;
const BIR = 10n ** BigInt(OLCEK);

function olcekli(d: Decimal): bigint {
  const neg = d.startsWith("-");
  const [tam = "0", kesir = ""] = d.replace(/^-/, "").split(".");
  const v = BigInt(tam) * BIR + BigInt((kesir + "0".repeat(OLCEK)).slice(0, OLCEK));
  return neg ? -v : v;
}

function olcektenOndalik(v: bigint, olcek: bigint, hane: number): Decimal | null {
  if (v % (olcek / 10n ** BigInt(hane)) !== 0n) return null;
  const neg = v < 0n;
  const m = neg ? -v : v;
  const tam = m / olcek;
  const kesir = (m % olcek).toString().padStart(olcek.toString().length - 1, "0").slice(0, hane).replace(/0+$/, "");
  return `${neg && m !== 0n ? "-" : ""}${tam.toString()}${kesir === "" ? "" : `.${kesir}`}`;
}

/** miktar × fiyat, 12 ondalık haneye sığmazsa `null` (yuvarlanmaz). */
export function carp(a: Decimal, b: Decimal): Decimal | null {
  const r = olcektenOndalik(olcekli(a) * olcekli(b), BIR * BIR, OLCEK);
  return r !== null && isValidDecimal(r) ? r : null;
}

export function esitMi(a: Decimal, b: Decimal): boolean {
  return olcekli(a) === olcekli(b);
}

function negatifMi(d: Decimal): boolean {
  return olcekli(d) < 0n;
}

function sifirMi(d: Decimal): boolean {
  return olcekli(d) === 0n;
}

function eksi(d: Decimal): Decimal {
  return olcektenOndalik(-olcekli(d), BIR, OLCEK) as Decimal;
}

/** Ondalık hane sayısı ("1.50" → 2). */
export function ondalikHane(d: Decimal): number {
  return d.split(".")[1]?.length ?? 0;
}

/** `hane` haneye yuvarlar (yarım sıfırdan uzağa). */
export function yuvarla(d: Decimal, hane: number): Decimal {
  const adim = 10n ** BigInt(OLCEK - hane);
  const v = olcekli(d);
  const m = v < 0n ? -v : v;
  const r = ((m + adim / 2n) / adim) * adim;
  return olcektenOndalik(v < 0n ? -r : r, BIR, OLCEK) as Decimal;
}

/**
 * Defterin saklayabildiği ondalık hane: miktar 9 (cihaz şeması v7); tutar,
 * masraf, vergi 8; fiyat 12 (`packages/local` SCALE_QUANTITY / SCALE_AMOUNT /
 * SCALE_PRICE). Fazlası yuvarlanmaz —
 * satır sorunlu olur; yoksa deneme yazımı bütün paketi reddeder.
 */
const HANE_MIKTAR = 9;
const HANE_TUTAR = 8;

// ---------------------------------------------------------------------------
// Sütunlar ve türler
// ---------------------------------------------------------------------------

export const SUTUNLAR = [
  { anahtar: "tarih", baslik: "Tarih", adlar: ["tarih", "islem tarihi", "date", "trade date"], zorunlu: true },
  { anahtar: "hesap", baslik: "Hesap", adlar: ["hesap", "hesap adi", "account"], zorunlu: true },
  { anahtar: "hesapKimligi", baslik: "Hesap kimliği", adlar: ["hesap kimligi", "hesap id", "account id"], zorunlu: false },
  { anahtar: "tur", baslik: "Tür", adlar: ["tur", "islem turu", "hareket turu", "type"], zorunlu: true },
  { anahtar: "varlik", baslik: "Varlık", adlar: ["varlik", "sembol", "kod", "symbol", "asset"], zorunlu: false },
  { anahtar: "piyasa", baslik: "Piyasa", adlar: ["piyasa", "borsa", "market", "exchange"], zorunlu: false },
  { anahtar: "miktar", baslik: "Miktar", adlar: ["miktar", "adet", "quantity"], zorunlu: false },
  { anahtar: "fiyat", baslik: "Fiyat", adlar: ["fiyat", "birim fiyat", "price"], zorunlu: false },
  { anahtar: "tutar", baslik: "Tutar", adlar: ["tutar", "amount"], zorunlu: false },
  { anahtar: "paraBirimi", baslik: "Para birimi", adlar: ["para birimi", "doviz", "currency"], zorunlu: true },
  { anahtar: "masraf", baslik: "Masraf", adlar: ["masraf", "komisyon", "fee"], zorunlu: false },
  { anahtar: "vergi", baslik: "Vergi", adlar: ["vergi", "stopaj", "tax"], zorunlu: false },
  { anahtar: "valor", baslik: "Valör", adlar: ["valor", "takas tarihi", "settle date"], zorunlu: false },
  { anahtar: "not", baslik: "Not", adlar: ["not", "aciklama", "note", "description"], zorunlu: false },
  { anahtar: "kaynakKimligi", baslik: "Kaynak kimliği", adlar: ["kaynak kimligi", "kaynak id", "id", "pk"], zorunlu: false },
  // Döviz alış/satış (22.10.2026): verilen taraf Tutar + Para birimi, alınan taraf bunlar.
  { anahtar: "alinanTutar", baslik: "Alınan tutar", adlar: ["alinan tutar"], zorunlu: false },
  { anahtar: "alinanParaBirimi", baslik: "Alınan para birimi", adlar: ["alinan para birimi"], zorunlu: false },
] as const;

export type SutunAnahtari = (typeof SUTUNLAR)[number]["anahtar"];

/** Başlık karşılaştırması: büyük/küçük harf, Türkçe harfler, boşluk ve noktalama önemsiz. */
export function sade(s: string): string {
  return s
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function basliklariEsle(baslik: readonly string[]): {
  readonly esleme: Readonly<Partial<Record<SutunAnahtari, number>>>;
  readonly bilinmeyen: readonly string[];
  readonly eksik: readonly string[];
} {
  const esleme: Partial<Record<SutunAnahtari, number>> = {};
  const bilinmeyen: string[] = [];
  baslik.forEach((ham, i) => {
    const s = sade(ham);
    const sutun = SUTUNLAR.find((c) => (c.adlar as readonly string[]).includes(s));
    if (sutun === undefined || esleme[sutun.anahtar] !== undefined) {
      if (ham.trim() !== "") bilinmeyen.push(ham.trim());
      return;
    }
    esleme[sutun.anahtar] = i;
  });
  const eksik = SUTUNLAR.filter((c) => c.zorunlu && esleme[c.anahtar] === undefined).map((c) => c.baslik);
  return { esleme, bilinmeyen, eksik };
}

/** Geri yatırılan temettü: Parafon'da İKİ hareket (temettü geliri + alış). */
export const GERI_YATIRIM = "TEMETTU_GERI_YATIRIM" as const;
export type Tur = TransactionType | typeof GERI_YATIRIM;

const TUR_ADLARI: readonly (readonly [Tur, readonly string[]])[] = [
  ["BUY", ["alis", "alim", "buy"]],
  ["SELL", ["satis", "satim", "sell"]],
  [GERI_YATIRIM, ["temettu geri yatirim", "temettu yeniden yatirim", "geri yatirilan temettu", "dividend reinvest", "reinvest"]],
  ["DIVIDEND", ["temettu", "kar payi", "dividend"]],
  ["INTEREST", ["faiz", "kupon", "interest"]],
  ["DEPOSIT", ["yatirma", "para yatirma", "deposit"]],
  ["WITHDRAWAL", ["cekme", "para cekme", "withdrawal"]],
  ["FEE", ["masraf", "komisyon", "fee"]],
  ["SPLIT", ["bolunme", "split"]],
  ["BONUS", ["bedelsiz", "bonus"]],
  ["RIGHTS", ["ruchan", "rights"]],
  ["TRANSFER_IN", ["devir giris", "virman giris", "transfer in"]],
  ["TRANSFER_OUT", ["devir cikis", "virman cikis", "transfer out"]],
  ["FX_CONVERSION", ["doviz alis", "doviz satis", "doviz alis satis", "doviz alim", "doviz satim"]],
  // Vergi ödemesi (25.10.2026): kaynak hesaptaki nakit, yıl ödeme tarihinin yılı.
  ["TAX_PAYMENT", ["vergi odemesi", "vergi odeme", "tax payment"]],
];

export function turOku(ham: string): Tur | null {
  const s = sade(ham);
  for (const [tur, adlar] of TUR_ADLARI) if (adlar.includes(s)) return tur;
  // Kodun kendisi de olur ("BUY").
  const kod = ham.trim().toUpperCase();
  return TUR_ADLARI.some(([t]) => t === kod) ? (kod as Tur) : null;
}

/** Nakit türleri: tek "Tutar" alanı (quantity = 1, price = tutar). */
const NAKIT: ReadonlySet<TransactionType> = new Set(["DIVIDEND", "INTEREST", "DEPOSIT", "WITHDRAWAL", "FEE", "FX_CONVERSION", "TAX_PAYMENT"]);
/** Varlık istemeyen nakit türleri (nakit varsayılanı). */
const VARLIKSIZ: ReadonlySet<TransactionType> = new Set(["INTEREST", "DEPOSIT", "WITHDRAWAL", "FEE", "FX_CONVERSION", "TAX_PAYMENT"]);
const FIYAT_ZORUNLU: ReadonlySet<Tur> = new Set(["BUY", "SELL", "RIGHTS", GERI_YATIRIM]);

// ---------------------------------------------------------------------------
// Satırlar
// ---------------------------------------------------------------------------

export interface VarlikAdi {
  /** Büyük harfli piyasa kodu ya da `null` ("ETF", "TEFAS", "BIST"…). */
  readonly piyasa: string | null;
  readonly kod: string;
}

/** Eşleştirme anahtarı: aynı piyasa + kod tek kez aranır. */
export function varlikAnahtari(v: VarlikAdi): string {
  return `${v.piyasa ?? ""}:${v.kod.toUpperCase()}`;
}

/** `ETF:SMH` → { ETF, SMH }; ayrı Piyasa sütunu önceliklidir. */
export function varlikOku(ham: string, piyasaHam: string): VarlikAdi | null {
  const s = ham.trim();
  if (s === "") return null;
  const m = /^([A-Za-zÇĞİÖŞÜçğıöşü]{2,10}):(.+)$/.exec(s);
  const piyasa = piyasaHam.trim() !== "" ? piyasaHam.trim().toLocaleUpperCase("tr") : (m?.[1]?.toLocaleUpperCase("tr") ?? null);
  return { piyasa, kod: (m?.[2] ?? s).trim() };
}

export interface OkunanSatir {
  /** Dosyadaki satır numarası (başlık 1). */
  readonly satirNo: number;
  readonly ham: readonly string[];
  readonly kaynakKimligi: string | null;
  readonly hesapAdi: string;
  readonly hesapKimligi: string | null;
  readonly tur: Tur;
  /** Desenden tanındıysa (alış + eksi masraf + "temettü"): önizlemede söylenir. */
  readonly desendenGeriYatirim: boolean;
  readonly varlik: VarlikAdi | null;
  readonly tarih: IsoDate;
  readonly valor: IsoDate | null;
  readonly miktar: Decimal | null;
  readonly fiyat: Decimal | null;
  readonly tutar: Decimal | null;
  readonly paraBirimi: string;
  readonly masraf: Decimal | null;
  readonly vergi: Decimal | null;
  readonly not: string | null;
  /** Yalnızca geri yatırılan temettüde: gelir olarak yazılacak tutar. */
  readonly temettuTutari: Decimal | null;
  /** Fiyat boş; o günün TEFAS fiyatı merkezin arşivinden alınacak (ekran). */
  readonly fiyatArsivden: boolean;
  /** Yalnızca döviz alış/satışta: alınan taraf (verilen: `tutar` + `paraBirimi`). */
  readonly alinanTutar: Decimal | null;
  readonly alinanParaBirimi: string | null;
}

export interface Sorun {
  readonly satirNo: number;
  readonly ham: readonly string[];
  readonly mesajlar: readonly string[];
  /**
   * Kurum ekstresindeki asıl satır (geri bildirim için, 25.10.2026): bölüm ve
   * sütun anahtarı → değer. Anahtarlar okuyucunun (Parafon'un) adları, değerler
   * belgenin; gönderilirken yalnızca değerler maskelenir. CSV'de yok (`ham`).
   */
  readonly kaynak?: { readonly bolum: string; readonly hucreler: readonly (readonly [string, string])[] };
}

export interface Okuma {
  readonly baslik: readonly string[];
  readonly ayrac: Ayrac;
  readonly satirlar: readonly OkunanSatir[];
  readonly sorunlar: readonly Sorun[];
  readonly bilinmeyenSutunlar: readonly string[];
  /** Zorunlu sütun eksikse dosya hiç okunmaz. */
  readonly eksikSutunlar: readonly string[];
  /** Sayı biçimi seçilmeden okunmuşsa öneri. */
  readonly bicimOnerisi: SayiBicimi | null;
}

const PARA_KODLARI = CURRENCIES.map((c) => c.code).join(", ");

function temettuNotu(not: string | null): boolean {
  return not !== null && /temett[uü]/i.test(not.toLocaleLowerCase("tr"));
}

export interface OkumaSecenekleri {
  /**
   * Alış/satışta boş fiyat, varlık TEFAS fonuysa sorun sayılmaz: o günün
   * fiyatı TEFAS arşivinden alınır (kullanıcı kararı 09.10.2026 — fonun günde
   * tek fiyatı var ve dosyadaki tarih fiyat tarihi). Yalnızca bunu bildiği
   * söylenen kaynak biçimleri açar.
   */
  readonly bosFiyatTefasArsivinden?: boolean;
}

export function dosyayiOku(metin: string, bicim: SayiBicimi, secenek: OkumaSecenekleri = {}): Okuma {
  const ayrac = ayracBul(metin);
  const tablo = csvOku(metin, ayrac);
  const baslik = tablo[0] ?? [];
  const { esleme, bilinmeyen, eksik } = basliklariEsle(baslik);
  const govde = tablo.slice(1);
  const al = (s: readonly string[], k: SutunAnahtari): string => {
    const i = esleme[k];
    return i === undefined ? "" : (s[i] ?? "").trim();
  };
  const sayisal: string[] = [];
  for (const s of govde) for (const k of ["miktar", "fiyat", "tutar", "masraf", "vergi"] as const) if (al(s, k) !== "") sayisal.push(al(s, k));
  const bicimOnerisi = sayiBicimiTahmin(sayisal);
  if (eksik.length > 0) {
    return { baslik, ayrac, satirlar: [], sorunlar: [], bilinmeyenSutunlar: bilinmeyen, eksikSutunlar: eksik, bicimOnerisi };
  }

  const satirlar: OkunanSatir[] = [];
  const sorunlar: Sorun[] = [];
  govde.forEach((s, i) => {
    const satirNo = i + 2;
    const m: string[] = [];
    const sayi = (k: SutunAnahtari, ad: string, hane = HANE_TUTAR): Decimal | null => {
      const v = sayiOku(al(s, k), bicim);
      if (v === undefined) {
        m.push(M.iceAktar.okuma.sayiOkunamadi(ad, al(s, k), bicim === "tr" ? "1.234,56" : "1,234.56"));
        return null;
      }
      if (v !== null && ondalikHane(v) > hane) {
        m.push(M.iceAktar.okuma.haneTasiyor(ad, ondalikHane(v), v, hane));
      }
      return v;
    };
    const tarih = tarihOku(al(s, "tarih"));
    if (tarih === null) m.push(M.iceAktar.okuma.tarihOkunamadi(al(s, "tarih")));
    const valorHam = al(s, "valor");
    const valor = valorHam === "" ? null : tarihOku(valorHam);
    if (valorHam !== "" && valor === null) m.push(M.iceAktar.okuma.valorOkunamadi(valorHam));
    if (valor !== null && tarih !== null && valor < tarih) m.push(M.iceAktar.okuma.valorOnce);
    const hesapAdi = al(s, "hesap");
    if (hesapAdi === "") m.push(M.iceAktar.okuma.hesapBos);
    let tur = turOku(al(s, "tur"));
    if (tur === null) m.push(M.iceAktar.okuma.turAnlasilmadi(al(s, "tur")));
    const paraBirimi = al(s, "paraBirimi").toUpperCase();
    if (!isKnownCurrency(paraBirimi)) m.push(M.iceAktar.okuma.paraBirimiYok(al(s, "paraBirimi"), PARA_KODLARI));
    const varlik = varlikOku(al(s, "varlik"), al(s, "piyasa"));
    const miktar = sayi("miktar", M.iceAktar.okuma.alan.miktar, HANE_MIKTAR);
    const fiyat = sayi("fiyat", M.iceAktar.okuma.alan.fiyat, OLCEK);
    const tutar = sayi("tutar", M.iceAktar.okuma.alan.tutar);
    let masraf = sayi("masraf", M.iceAktar.okuma.alan.masraf);
    const vergi = sayi("vergi", M.iceAktar.okuma.alan.vergi);
    const notHam = al(s, "not");
    const not = notHam === "" ? null : notHam;

    // Geri yatırılan temettünün kaynaktaki deseni: alış + eksi masraf + "temettü".
    // Temettü kuruşlu gelir, alınan pay ise ondan bölünerek bulunur: miktar ×
    // fiyat masrafın hanesine (en az kuruş) yuvarlanınca eksi masrafa eşit
    // olmalı. Gelir masraftaki tutardır (kaynağın kendi parası).
    let desendenGeriYatirim = false;
    let temettuTutari: Decimal | null = null;
    let fiyatArsivden = false;
    if (tur === "BUY" && masraf !== null && negatifMi(masraf)) {
      const alisTutari = miktar !== null && fiyat !== null ? carp(miktar, fiyat) : null;
      const hane = Math.max(2, ondalikHane(masraf));
      if (!temettuNotu(not)) m.push(M.iceAktar.okuma.masrafEksiTemettusuz);
      else if (alisTutari === null || !esitMi(eksi(masraf), yuvarla(alisTutari, hane))) {
        m.push(M.iceAktar.okuma.eksiMasrafTutmuyor(masraf, alisTutari ?? "?"));
      } else {
        tur = GERI_YATIRIM;
        temettuTutari = eksi(masraf);
        masraf = null;
        desendenGeriYatirim = true;
      }
    } else if (masraf !== null && negatifMi(masraf)) m.push(M.iceAktar.okuma.masrafEksi);
    else if (tur === "BUY" && temettuNotu(not)) {
      // Açıklama temettü diyor ama gelirin tutarı (eksi masraf) yok: alış mı,
      // geri yatırım mı bilinmez. Sıradan alış saymak temettü gelirini siler ve
      // olmayan bir para çıkışı yazar — tahmin yok, satır sorunlu. Kullanıcı
      // kararı (09.10.2026): kendiliğinden yorumlanmaz, kaynakta düzeltilir.
      m.push(M.iceAktar.okuma.temettuBelirsiz);
    }
    if (vergi !== null && negatifMi(vergi)) m.push(M.iceAktar.okuma.vergiEksi);
    const alinanTutar = sayi("alinanTutar", M.iceAktar.okuma.alan.alinanTutar);
    const alinanPbHam = al(s, "alinanParaBirimi").toUpperCase();
    const alinanParaBirimi = alinanPbHam === "" ? null : alinanPbHam;
    if (tur === "FX_CONVERSION") {
      if (alinanTutar === null) m.push(M.iceAktar.okuma.alinanTutarGerekli);
      else if (negatifMi(alinanTutar) || sifirMi(alinanTutar)) m.push(M.iceAktar.okuma.alinanTutarSifir);
      if (alinanParaBirimi === null || !isKnownCurrency(alinanParaBirimi)) {
        m.push(M.iceAktar.okuma.alinanParaBirimiYok(al(s, "alinanParaBirimi"), PARA_KODLARI));
      } else if (alinanParaBirimi === paraBirimi) m.push(M.iceAktar.okuma.alinanParaBirimiAyni);
    } else if (alinanTutar !== null || alinanParaBirimi !== null) {
      m.push(M.iceAktar.okuma.alinanYalnizDoviz);
    }

    if (tur !== null) {
      const nakit = tur !== GERI_YATIRIM && NAKIT.has(tur);
      if (nakit) {
        if (tutar === null) m.push(M.iceAktar.okuma.tutarGerekli);
        else if (negatifMi(tutar) || sifirMi(tutar)) m.push(M.iceAktar.okuma.tutarSifir);
        if (tur === "DIVIDEND" && varlik === null) m.push(M.iceAktar.okuma.temettudeVarlik);
      } else {
        if (varlik === null) m.push(M.iceAktar.okuma.varlikGerekli);
        if (miktar === null) m.push(M.iceAktar.okuma.miktarGerekli);
        else if (tur !== "SPLIT" && (negatifMi(miktar) || sifirMi(miktar))) m.push(M.iceAktar.okuma.miktarSifir);
        if (FIYAT_ZORUNLU.has(tur) && fiyat === null) {
          if (secenek.bosFiyatTefasArsivinden === true && (tur === "BUY" || tur === "SELL") && varlik?.piyasa === "TEFAS") fiyatArsivden = true;
          else m.push(M.iceAktar.okuma.fiyatGerekli);
        }
        if (fiyat !== null && negatifMi(fiyat)) m.push(M.iceAktar.okuma.fiyatEksi);
        if (tur === GERI_YATIRIM && !desendenGeriYatirim && miktar !== null && fiyat !== null) {
          temettuTutari = carp(miktar, fiyat);
          if (temettuTutari === null) m.push(M.iceAktar.okuma.carpimSigmiyor);
        }
      }
    }
    if (m.length > 0 || tarih === null || tur === null) {
      sorunlar.push({ satirNo, ham: s, mesajlar: m });
      return;
    }
    satirlar.push({
      satirNo,
      ham: s,
      kaynakKimligi: al(s, "kaynakKimligi") === "" ? null : al(s, "kaynakKimligi"),
      hesapAdi,
      hesapKimligi: al(s, "hesapKimligi") === "" ? null : al(s, "hesapKimligi"),
      tur,
      desendenGeriYatirim,
      varlik: tur !== GERI_YATIRIM && VARLIKSIZ.has(tur) ? null : varlik,
      tarih,
      valor,
      miktar,
      fiyat,
      tutar,
      paraBirimi,
      masraf,
      vergi,
      not,
      temettuTutari,
      fiyatArsivden,
      alinanTutar: tur === "FX_CONVERSION" ? alinanTutar : null,
      alinanParaBirimi: tur === "FX_CONVERSION" ? alinanParaBirimi : null,
    });
  });
  // Aynı kaynak kimliği iki kez: ikisi de sorunlu (hangisinin doğru olduğu bilinmez).
  const sayac = new Map<string, number>();
  for (const s of satirlar) if (s.kaynakKimligi !== null) sayac.set(s.kaynakKimligi, (sayac.get(s.kaynakKimligi) ?? 0) + 1);
  const temiz = satirlar.filter((s) => {
    if (s.kaynakKimligi === null || (sayac.get(s.kaynakKimligi) ?? 0) < 2) return true;
    sorunlar.push({ satirNo: s.satirNo, ham: s.ham, mesajlar: [M.iceAktar.okuma.kimlikTekrar(s.kaynakKimligi)] });
    return false;
  });
  sorunlar.sort((a, b) => a.satirNo - b.satirNo);
  return { baslik, ayrac, satirlar: temiz, sorunlar, bilinmeyenSutunlar: bilinmeyen, eksikSutunlar: [], bicimOnerisi };
}

// ---------------------------------------------------------------------------
// Kalıcı kimlikler
// ---------------------------------------------------------------------------

const B32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

async function ozet(metin: string): Promise<Uint8Array> {
  return new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(metin)));
}

function b32(baytlar: Uint8Array, uzunluk: number): string {
  let out = "";
  for (let i = 0; i < uzunluk; i += 1) out += B32[(baytlar[i] ?? 0) % 32];
  return out;
}

/** ULID biçimi: zaman kısmı işlem gününden (sıralama korunur), kalanı özetten. */
function zamanKismi(tarih: IsoDate): string {
  let t = Date.parse(`${tarih}T00:00:00Z`);
  let out = "";
  for (let i = 0; i < 10; i += 1) {
    out = B32[t % 32] + out;
    t = Math.floor(t / 32);
  }
  return out;
}

/** Hesabın kalıcı anahtarı: kimlik verilmişse o, yoksa ad (büyük/küçük harf duyarsız). */
export function hesapAnahtari(s: Pick<OkunanSatir, "hesapAdi" | "hesapKimligi">): string {
  return s.hesapKimligi !== null ? `k:${s.hesapKimligi}` : `a:${s.hesapAdi.toLocaleLowerCase("tr")}`;
}

export async function hesapKimligiTuret(anahtar: string): Promise<string> {
  return `ice-${b32(await ozet(`parafon-ice-hesap:${anahtar}`), 26)}`;
}

/**
 * Hareket kimlikleri. Kaynak kimliği varsa ondan; yoksa satırın içeriğinden ve
 * aynı içerikli satırlar arasındaki sırasından — aynı dosya ikinci kez
 * yüklenince aynı kimlikler çıkar, aynı gün aynı fiyattan iki alış da ayrı kalır.
 * Geri yatırılan temettü iki kimlik alır.
 */
export async function hareketKimlikleri(satirlar: readonly OkunanSatir[]): Promise<Map<number, readonly string[]>> {
  const out = new Map<number, readonly string[]>();
  const sira = new Map<string, number>();
  for (const s of satirlar) {
    let kok: string;
    if (s.kaynakKimligi !== null) kok = `k:${s.kaynakKimligi}`;
    else {
      const icerik = JSON.stringify(s.ham.map((a) => a.trim()));
      const n = (sira.get(icerik) ?? 0) + 1;
      sira.set(icerik, n);
      kok = `i:${icerik}#${n}`;
    }
    const zaman = zamanKismi(s.tarih);
    const kimlik = async (ek: string) => zaman + b32(await ozet(`parafon-ice-hareket:${kok}${ek}`), 16);
    out.set(s.satirNo, s.tur === GERI_YATIRIM ? [await kimlik(":temettu"), await kimlik(":alis")] : [await kimlik("")]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Şablon
// ---------------------------------------------------------------------------

export function sablonCsv(): string {
  const b = SUTUNLAR.map((c) => c.baslik);
  const ornek = [
    ["15.03.2025", "Aracı Kurum A", "", "Alış", "GTL", "TEFAS", "100", "1,50", "", "TRY", "", "", "", "", "1001", "", ""],
    ["18.03.2025", "Aracı Kurum B", "", "Döviz alış", "", "", "", "", "3.650,00", "TRY", "", "", "", "", "1005", "100,00", "USD"],
    ["20.03.2025", "Aracı Kurum B", "", "Alış", "ETF:SMH", "", "2", "250,00", "", "USD", "1,00", "", "", "", "1002", "", ""],
    ["15.06.2025", "Aracı Kurum B", "", "Temettü (geri yatırım)", "ETF:SMH", "", "0,04", "260,00", "", "USD", "", "", "", "temettü", "1003", "", ""],
    ["01.07.2025", "Aracı Kurum A", "", "Satış", "GTL", "TEFAS", "40", "1,80", "", "TRY", "", "", "", "", "1004", "", ""],
  ];
  return csvYaz([b, ...ornek]);
}
